import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import PageHeader from 'src/components/organisms/PageHeader';
import Footer from 'src/components/molecules/footer/Footer';
import CustomButton from 'src/components/atoms/buttons/CustomButton';
import { CardType, ContentCard } from 'src/components/organisms/ContentCard';
import { CustomDataGrid } from 'src/components/organisms/CustomDataGrid';
import DetailPanelContainer from 'src/components/organisms/tables/DetailPanelContainer';
import { useGetStorageItem, useSearchStorageItem, useStockCorrection } from 'src/hooks/api/requestConfigs/warehouse';
import { useStocktakingTable, useWarehouseItemLocationsTable } from 'src/hooks/table/configs/useStocktakingTable';
import type { Identifier, StockCorrectionItem, WarehouseItemLocation } from 'src/hooks/table/configs/useStocktakingTable';
import { useStockCorrectionLocationsTable } from 'src/hooks/table/configs/useProductLocationsTable';
import type { ProductLocationRow } from 'src/hooks/table/configs/useProductLocationsTable';
import WarehouseSelector, { useActiveWarehouse, WarehouseSelectionPopup } from 'src/components/atoms/WarehouseSelector';
import { ENV, TENANT } from 'src/utils/enviromentValues';

type CorrectionPayload = NonNullable<Parameters<typeof useStockCorrection>[1]>;
type CorrectionValues = { LocationId: Identifier | ''; LocationQty: string; Comment: string };
type SearchRequest = { key: string; term: string };
type StorageItemRequest = { key: string; row: ProductLocationRow; manufacturerProductId: Identifier };
type CorrectionRequest = { key: string; payload: CorrectionPayload };
type SavedCorrection = { quantity: number; location: string };
type Message = { severity: 'error' | 'info'; text: string };
type RequestResult = { rows: unknown; loading: boolean; status: string | number | null; abort: () => void };
type RequestComplete = (rows: unknown, status: RequestResult['status']) => void;

const emptyValues: CorrectionValues = { LocationId: '', LocationQty: '', Comment: '' };
const emptyLocations: WarehouseItemLocation[] = [];
const imageFormats = ['jpg', 'png', 'jpeg'];
const formatNumber = (value: number) => value.toLocaleString(undefined, { maximumFractionDigits: 20 });
const sameId = (first: unknown, second: unknown) => first != null && second != null && String(first) === String(second);
const hasIdentifier = (value: unknown): value is Identifier => (typeof value === 'string' && value.trim() !== '') || (typeof value === 'number' && Number.isFinite(value));
const isSuccessfulStatus = (status: RequestResult['status']) => (Number(status) >= 200 && Number(status) < 300) || /^(success|succeeded|complete|completed)$/i.test(String(status ?? ''));
const isFailedStatus = (status: RequestResult['status']) => (Number.isFinite(Number(status)) && Number(status) >= 400) || /error|fail|abort|cancel|timeout/i.test(String(status ?? ''));
const isStockCorrectionItem = (value: unknown): value is StockCorrectionItem => {
    if (value == null || typeof value !== 'object') return false;
    const item = value as Partial<StockCorrectionItem>;
    return hasIdentifier(item.StorageItemId) && hasIdentifier(item.WarehouseItemId) && hasIdentifier(item.ProductId) && hasIdentifier(item.WarehouseId) && Array.isArray(item.WarehouseItemLocations);
};
const getItems = (value: unknown): StockCorrectionItem[] | null => {
    const items = Array.isArray(value) ? value : value == null ? null : [value];
    return items?.every(isStockCorrectionItem) ? items : null;
};
const getCorrectionError = (item: StockCorrectionItem | null, values: CorrectionValues) => {
    if (!item) return 'Scan and select a product first.';
    if (!item.WarehouseItemLocations.some((location) => sameId(location.LocationId, values.LocationId))) return 'Select a location.';
    if (values.LocationQty.trim() === '' || !Number.isFinite(Number(values.LocationQty)) || Number(values.LocationQty) < 0) return 'Enter a quantity of zero or more.';
    return null;
};

type LayoutProps = {
    warehouseId: Identifier | null;
    onWarehouseChange: (value: Identifier) => void;
    scanner?: ReactNode;
    correctionAction?: ReactNode;
    isSaving?: boolean;
    children: ReactNode;
    dialog?: ReactNode;
};

const StockCorrectionLayout = ({ warehouseId, onWarehouseChange, scanner, correctionAction, isSaving = false, children, dialog }: LayoutProps) => (
    <>
        <PageHeader {...{ title: 'Stock Correction', subTitle: 'Scan a product and correct its quantity at a warehouse location', components: [
            ...(scanner ? [{ position: 'center', component: scanner }] : []),
            { component: <WarehouseSelector value={warehouseId} onChange={onWarehouseChange} disabled={isSaving} /> },
            { component: correctionAction ?? <Button variant="contained" disabled>Correct stock</Button> },
        ] }} />
        <Box sx={{ px: { xs: 2, md: 3 }, pb: 3, display: 'grid', gap: 2, minWidth: 0 }}>{children}</Box>
        {dialog}
        <Footer />
    </>
);

export const StockCorrection = () => {
    const { warehouseId, selectWarehouse: onWarehouseChange } = useActiveWarehouse();

    if (warehouseId == null || warehouseId === '') return (
        <StockCorrectionLayout warehouseId={null} onWarehouseChange={onWarehouseChange} dialog={<WarehouseSelectionPopup />}>
            <ContentCard {...{ cardId: 'StockCorrection', md: 12, cardTypes: [CardType.noHeader] }}><Typography>Select a warehouse to scan products and correct stock.</Typography></ContentCard>
        </StockCorrectionLayout>
    );

    // Switching warehouses discards the previous form, matches and requests.
    return <StockCorrectionStation key={String(warehouseId)} warehouseId={warehouseId} onWarehouseChange={onWarehouseChange} />;
};

const StockCorrectionStation = ({ warehouseId, onWarehouseChange }: { warehouseId: Identifier; onWarehouseChange: (value: Identifier) => void }) => {
    const [selectedItem, setSelectedItem] = useState<StockCorrectionItem | null>(null);
    const selectedItemRef = useRef<StockCorrectionItem | null>(null);
    const [values, setValues] = useState<CorrectionValues>(emptyValues);
    const valuesRef = useRef<CorrectionValues>(emptyValues);
    const [scannerValue, setScannerValue] = useState('');
    const scannerValueRef = useRef('');
    const scannerRef = useRef<HTMLInputElement>(null);
    const [quantityScanMode, setQuantityScanMode] = useState(false);
    const quantityScanModeRef = useRef(false);
    const [searchRequest, setSearchRequest] = useState<SearchRequest | null>(null);
    const searchRequestRef = useRef<SearchRequest | null>(null);
    const [storageItemRequest, setStorageItemRequest] = useState<StorageItemRequest | null>(null);
    const storageItemRequestRef = useRef<StorageItemRequest | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [correctionRequest, setCorrectionRequest] = useState<CorrectionRequest | null>(null);
    const correctionRequestRef = useRef<CorrectionRequest | null>(null);
    const requestSequenceRef = useRef(0);
    const [matches, setMatches] = useState<{ scan: string; rows: StockCorrectionItem[] } | null>(null);
    const [savedCorrection, setSavedCorrection] = useState<SavedCorrection | null>(null);
    const [message, setMessage] = useState<Message | null>(null);
    const isSaving = correctionRequest != null;
    const isSearching = searchRequest != null || storageItemRequest != null;
    const locations = selectedItem?.WarehouseItemLocations ?? emptyLocations;
    const selectedLocation = locations.find((location) => sameId(location.LocationId, values.LocationId));
    const [locationColumns, locationTableSettings] = useWarehouseItemLocationsTable(locations);

    const updateValues = useCallback((next: CorrectionValues) => { valuesRef.current = next; setValues(next); }, []);
    const updateQuantityScanMode = useCallback((active: boolean) => { quantityScanModeRef.current = active; setQuantityScanMode(active); }, []);
    const clearScanner = useCallback(() => { scannerValueRef.current = ''; setScannerValue(''); }, []);

    const clearForm = useCallback(() => {
        if (correctionRequestRef.current) return;
        searchRequestRef.current = null;
        setSearchRequest(null);
        storageItemRequestRef.current = null;
        setStorageItemRequest(null);
        selectedItemRef.current = null;
        setSelectedItem(null);
        updateValues({ ...emptyValues });
        updateQuantityScanMode(false);
        clearScanner();
        setMatches(null);
        setSavedCorrection(null);
        setMessage(null);
        scannerRef.current?.focus();
    }, [clearScanner, updateQuantityScanMode, updateValues]);

    const selectItem = useCallback((item: StockCorrectionItem) => {
        selectedItemRef.current = item;
        setSelectedItem(item);
        const location = item.WarehouseItemLocations.length === 1 ? item.WarehouseItemLocations[0] : null;
        updateValues({ LocationId: location?.LocationId ?? '', LocationQty: location ? String(location.Quantity) : '', Comment: '' });
        updateQuantityScanMode(location != null);
        setMatches(null);
        setSavedCorrection(null);
        setMessage(null);
    }, [updateQuantityScanMode, updateValues]);

    const handleTableSelect = useCallback((row: ProductLocationRow) => {
        if (correctionRequestRef.current) return;
        clearForm();
        if (!sameId(row.WarehouseId, warehouseId) || !hasIdentifier(row.ProductId) || !hasIdentifier(row.ManufacturerProductId) || !hasIdentifier(row.LocationId)) {
            setMessage({ severity: 'error', text: 'This row is missing the identifiers needed to load its storage item.' });
            return;
        }
        const request = { key: `${Date.now()}-${++requestSequenceRef.current}`, row, manufacturerProductId: row.ManufacturerProductId };
        storageItemRequestRef.current = request;
        setStorageItemRequest(request);
    }, [clearForm, warehouseId]);

    const [productColumns, productTableSettings, productTableData] = useStockCorrectionLocationsTable(warehouseId, refreshKey, handleTableSelect, isSaving || isSearching);
    const productTableUnavailable = productTableSettings.loading || isFailedStatus(productTableData.status);

    const editValues = (changes: Partial<CorrectionValues>) => {
        updateValues({ ...valuesRef.current, ...changes });
        setSavedCorrection(null);
        setMessage(null);
    };

    const selectLocation = (id: Identifier) => {
        const location = selectedItemRef.current?.WarehouseItemLocations.find((row) => sameId(row.LocationId, id));
        editValues({ LocationId: location?.LocationId ?? '', LocationQty: location ? String(location.Quantity) : '' });
        updateQuantityScanMode(location != null);
        scannerRef.current?.focus();
    };

    const submitCorrection = () => {
        if (correctionRequestRef.current || searchRequestRef.current || storageItemRequestRef.current) return;
        const item = selectedItemRef.current;
        const currentValues = valuesRef.current;
        const error = getCorrectionError(item, currentValues);
        if (error || !item) { setMessage({ severity: 'error', text: error ?? 'Select a product.' }); return; }
        const location = item.WarehouseItemLocations.find((row) => sameId(row.LocationId, currentValues.LocationId))!;
        // Refs also cover scans arriving before React has rendered the latest quantity.
        const payload: CorrectionPayload = { ProductId: item.ProductId, LocationId: location.LocationId, LocationQty: Number(currentValues.LocationQty), StockQuantityChangeReason: 1, Comment: currentValues.Comment.trim() };
        const request = { key: `${Date.now()}-${++requestSequenceRef.current}`, payload };
        correctionRequestRef.current = request;
        setCorrectionRequest(request);
        setSavedCorrection(null);
        setMessage(null);
        clearScanner();
    };

    const handleSearchComplete: RequestComplete = (rows, status) => {
        const request = searchRequestRef.current;
        if (!request || request.key !== searchRequest?.key) return;
        searchRequestRef.current = null;
        setSearchRequest(null);
        const items = getItems(rows);
        if (isFailedStatus(status) || !items || items.some((item) => !sameId(item.WarehouseId, warehouseId))) {
            setMessage({ severity: 'error', text: 'The product search could not be completed. Scan the ID again to retry.' });
        } else if (items.length === 0) {
            setMessage({ severity: 'info', text: `No products found for "${request.term}" in the selected warehouse.` });
        } else if (items.length === 1) {
            selectItem(items[0]);
        } else {
            setMatches({ scan: request.term, rows: items });
        }
    };

    const handleStorageItemComplete: RequestComplete = (rows, status) => {
        const request = storageItemRequestRef.current;
        if (!request || request.key !== storageItemRequest?.key) return;
        storageItemRequestRef.current = null;
        setStorageItemRequest(null);
        const items = getItems(rows);
        const item = items?.length === 1 ? items[0] : null;
        const location = item?.WarehouseItemLocations.find((row) => sameId(row.LocationId, request.row.LocationId));
        if (isFailedStatus(status) || !item || !sameId(item.ProductId, request.row.ProductId) || !sameId(item.ManufacturerProductId, request.manufacturerProductId) || !sameId(item.WarehouseId, warehouseId) || !location) {
            setMessage({ severity: 'error', text: 'The selected storage item and location could not be loaded. Select the row again to retry.' });
            return;
        }
        selectItem(item);
        updateValues({ LocationId: location.LocationId, LocationQty: String(location.Quantity), Comment: '' });
        updateQuantityScanMode(true);
    };

    const handleCorrectionComplete: RequestComplete = (rows, status) => {
        const request = correctionRequestRef.current;
        const previous = selectedItemRef.current;
        if (!request || request.key !== correctionRequest?.key || !previous) return;
        correctionRequestRef.current = null;
        setCorrectionRequest(null);
        const items = getItems(rows);
        const item = items?.find((row) => sameId(row.StorageItemId, previous.StorageItemId) && sameId(row.WarehouseItemId, previous.WarehouseItemId) && sameId(row.ProductId, previous.ProductId) && sameId(row.WarehouseId, warehouseId));
        const location = item?.WarehouseItemLocations.find((row) => sameId(row.LocationId, request.payload.LocationId));
        if (isFailedStatus(status) || !item || !location || !Number.isFinite(location.Quantity)) {
            setMessage({ severity: 'error', text: 'The correction could not be confirmed. Scan the product again to verify its stock before retrying.' });
            updateQuantityScanMode(false);
            return;
        }
        // A correction response may omit the image ID, so preserve the loaded image.
        const updatedItem = { ...previous, ...item, DefaultPictureResourceId: item.DefaultPictureResourceId === undefined ? previous.DefaultPictureResourceId : item.DefaultPictureResourceId };
        selectedItemRef.current = updatedItem;
        setSelectedItem(updatedItem);
        updateValues({ ...valuesRef.current, LocationId: location.LocationId, LocationQty: String(location.Quantity) });
        setSavedCorrection({ quantity: location.Quantity, location: location.LocationLabel });
        setRefreshKey((key) => key + 1);
        setMessage(null);
        updateQuantityScanMode(false);
        clearScanner();
    };

    const handleScannerSubmit = (submittedValue = scannerValueRef.current) => {
        if (correctionRequestRef.current) return;
        const scan = submittedValue.trim();
        if (!scan) return;
        clearScanner();
        const command = scan.toLowerCase();
        if (command === 'cancel') { clearForm(); return; }
        if (command === 'ok') { submitCorrection(); return; }

        const item = selectedItemRef.current;
        if (quantityScanModeRef.current && item) {
            // Check the active product first: its numeric ID must not become a quantity.
            if (sameId(item.ProductId, scan) || sameId(item.ManufacturerProductId, scan)) {
                const currentQuantity = Number(valuesRef.current.LocationQty);
                editValues({ LocationQty: String((Number.isFinite(currentQuantity) ? currentQuantity : 0) + 1) });
                return;
            }
            const quantity = Number(scan);
            if (Number.isFinite(quantity) && quantity >= 0 && quantity <= 1000) {
                editValues({ LocationQty: String(quantity) });
                return;
            }
        }

        clearForm();
        const request = { key: `${Date.now()}-${++requestSequenceRef.current}`, term: scan };
        searchRequestRef.current = request;
        setSearchRequest(request);
    };

    useEffect(() => {
        if (!isSaving && !isSearching && !matches) scannerRef.current?.focus();
    }, [isSaving, isSearching, matches, selectedItem?.WarehouseItemId]);

    // Show the difference between the counted and recorded quantity so mistakes stand out before saving.
    const newQuantity = values.LocationQty.trim() === '' ? NaN : Number(values.LocationQty);
    const quantityChange = selectedLocation && Number.isFinite(newQuantity) ? newQuantity - selectedLocation.Quantity : null;
    const quantityChangeText = quantityChange == null ? 'Counted total at this location.' : quantityChange === 0 ? 'No change' : `Change: ${quantityChange > 0 ? '+' : '−'}${formatNumber(Math.abs(quantityChange))}`;
    const quantityChangeColor = quantityChange == null || quantityChange === 0 ? 'text.secondary' : quantityChange > 0 ? 'success.main' : 'warning.main';

    const canCorrect = !isSaving && !isSearching && getCorrectionError(selectedItem, values) == null;
    const scanner = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField inputRef={scannerRef} disabled={isSaving} value={scannerValue} onChange={(event) => { scannerValueRef.current = event.target.value; setScannerValue(event.target.value); }} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); handleScannerSubmit((event.target as HTMLInputElement).value); } }} placeholder={quantityScanMode ? 'Scan quantity, Product ID, OK or Cancel' : 'Scan Product ID or Manufacturer Product ID'} inputProps={{ 'aria-label': 'Scan product ID, manufacturer product ID, quantity, OK or Cancel' }} autoFocus sx={{ width: { xs: 180, sm: 300, md: 380 } }} />
            <CustomButton disabled={isSaving} title="Submit scan" size="small" iconButton variant="outlined" icon={<ArrowForwardIcon />} onClick={() => handleScannerSubmit()} />
            {isSearching && <CircularProgress size={20} aria-label="Searching for products" />}
        </Box>
    );

    return (
        <>
            {searchRequest && <StorageItemSearch key={searchRequest.key} request={searchRequest} warehouseId={warehouseId} onComplete={handleSearchComplete} />}
            {storageItemRequest && <StorageItemLookup key={storageItemRequest.key} request={storageItemRequest} warehouseId={warehouseId} onComplete={handleStorageItemComplete} />}
            {correctionRequest && <StockCorrectionRequest key={correctionRequest.key} request={correctionRequest} warehouseId={warehouseId} onComplete={handleCorrectionComplete} />}
            <StockCorrectionLayout warehouseId={warehouseId} onWarehouseChange={(value) => { if (!correctionRequestRef.current) onWarehouseChange(value); }} scanner={scanner} isSaving={isSaving} correctionAction={<CustomButton title="Correct stock" icon={<SaveOutlinedIcon />} isSubmitting={isSaving} disabled={!canCorrect} onClick={submitCorrection} />} dialog={matches && <StorageItemPicker scan={matches.scan} rows={matches.rows} onSelect={selectItem} onClose={clearForm} />}>
                {message && <ContentCard key="message" {...{ cardId: 'StockCorrectionMessage', md: 12, cardTypes: [CardType.noHeader] }}><Alert severity={message.severity}>{message.text}</Alert></ContentCard>}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 5fr) minmax(0, 7fr)' }, gap: 2, alignItems: 'start', minWidth: 0 }}>
                    <Box sx={{ minWidth: 0 }}>
                        <ContentCard key="product" {...{ cardId: 'Product', md: 12, components: [{ isInGroupedMenu: false, component: <CustomButton title="Clear" variant="outlined" icon={<ClearIcon />} disabled={isSaving} onClick={clearForm} /> }] }}>
                            <Box sx={{ display: 'grid', gap: 2, width: '100%' }}>
                                <ProductImage key={String(selectedItem?.DefaultPictureResourceId ?? 'none')} imgId={selectedItem?.DefaultPictureResourceId} productName={selectedItem?.ProductName} />
                                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3, overflowWrap: 'anywhere', color: selectedItem ? 'text.primary' : 'text.secondary' }}>
                                    {selectedItem?.ProductName ?? 'No product scanned'}
                                </Typography>
                                <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 2, rowGap: 1.5, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                    <DetailItem label="Product ID" value={selectedItem?.ProductId} />
                                    <DetailItem label="Manufacturer Product ID" value={selectedItem?.ManufacturerProductId} />
                                    <DetailItem label="Warehouse" value={selectedItem?.WarehouseName} />
                                    <DetailItem label="Warehouse quantity" value={selectedItem?.WarehouseQty} emphasis />
                                </Box>
                            </Box>
                        </ContentCard>
                    </Box>
                    <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
                        <ContentCard key="correction" {...{ cardId: 'Stock correction', md: 12, components: selectedItem ? [{ isInGroupedMenu: false, component: <CorrectionStatus saving={isSaving} saved={savedCorrection} /> }] : [] }}>
                            <Box component="form" onSubmit={(event) => { event.preventDefault(); submitCorrection(); }} noValidate sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2, width: '100%' }}>
                                <TextField fullWidth select label="Location" name="LocationId" value={values.LocationId} onChange={(event) => selectLocation(event.target.value)} disabled={!selectedItem || locations.length === 0 || isSaving} helperText={selectedItem && locations.length === 0 ? 'This item has no warehouse locations to correct.' : 'The location whose stock you counted.'} sx={{ gridColumn: '1 / -1' }}>
                                    <MenuItem value="" disabled>Select a location</MenuItem>
                                    {locations.map((location) => <MenuItem key={location.LocationId} value={location.LocationId}>{location.LocationLabel}{location.PartitionName ? ` (${location.PartitionName})` : ''}</MenuItem>)}
                                </TextField>
                                <TextField fullWidth label="Current quantity" value={selectedLocation?.Quantity ?? ''} InputProps={{ readOnly: true }} disabled={!selectedLocation} inputProps={{ style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }} helperText="Recorded now." />
                                <TextField fullWidth name="LocationQty" label="New quantity" value={values.LocationQty} onChange={(event) => { editValues({ LocationQty: event.target.value }); updateQuantityScanMode(true); }} type="number" disabled={!selectedLocation || isSaving} inputProps={{ min: 0, step: 'any', style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }} helperText={quantityChangeText} FormHelperTextProps={{ sx: { color: quantityChangeColor, fontWeight: quantityChange ? 600 : 400 } }} sx={{ '& .MuiOutlinedInput-root:not(.Mui-disabled)': { bgcolor: 'action.selected', fontWeight: 700, fontSize: '1.15rem' }, '& .MuiOutlinedInput-root:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main', borderWidth: 2 }, '& .MuiInputLabel-root:not(.Mui-disabled)': { color: 'primary.main', fontWeight: 600 } }} />
                                <TextField fullWidth name="Comment" label="Comment (optional)" value={values.Comment} onChange={(event) => editValues({ Comment: event.target.value })} multiline minRows={2} disabled={!selectedItem || isSaving} sx={{ gridColumn: '1 / -1' }} />
                            </Box>
                        </ContentCard>

                        <ContentCard key="locations" {...{ cardId: 'Warehouse item locations', md: 12, contentPadding: 0 }}>
                            {selectedItem
                                ? <Box sx={{ width: '100%', minWidth: 0, maxHeight: { xs: 360, md: 440 }, overflow: 'auto', overscrollBehavior: 'contain' }}><CustomDataGrid columns={locationColumns} tableSettings={locationTableSettings} /></Box>
                                : <Typography color="text.secondary" sx={{ p: 2 }}>Scan a product to see its warehouse locations.</Typography>}
                        </ContentCard>
                    </Box>
                </Box>
                <ContentCard key="stock-correction-locations" {...{ cardId: 'Stock correction locations', md: 12, contentPadding: 0 }}>
                    <CustomDataGrid columns={productColumns} tableSettings={productTableSettings} onRowClick={({ row }: { row: ProductLocationRow }) => { if (!isSaving && !isSearching && !productTableUnavailable) handleTableSelect(row); }} />
                </ContentCard>
            </StockCorrectionLayout>
        </>
    );
};

const DetailItem = ({ label, value, emphasis = false }: { label: string; value?: ReactNode; emphasis?: boolean }) => {
    const isEmpty = value == null || value === '';
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography component="dt" variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>{label}</Typography>
            <Typography component="dd" sx={{ m: 0, fontWeight: emphasis ? 700 : 500, fontSize: emphasis ? '1.25rem' : '0.95rem', lineHeight: 1.3, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere', color: isEmpty ? 'text.disabled' : 'text.primary' }}>
                {isEmpty ? '—' : value}
            </Typography>
        </Box>
    );
};

// Keyed request components isolate completion and abort handling for each selection.
// Loading can end before useResponse publishes rows; wait for status and data.
const useRequestCompletion = (result: RequestResult, onComplete: RequestComplete) => {
    const completedRef = useRef(false);
    const abortRef = useRef(result.abort);
    abortRef.current = result.abort;

    useEffect(() => {
        if (completedRef.current || result.loading) return;
        const failed = isFailedStatus(result.status);
        if (!failed && (!isSuccessfulStatus(result.status) || result.rows == null)) return;
        completedRef.current = true;
        onComplete(result.rows, result.status);
    }, [result.loading, result.rows, result.status, onComplete]);

    useEffect(() => () => abortRef.current(), []);
};

const StorageItemSearch = ({ request, warehouseId, onComplete }: { request: SearchRequest; warehouseId: Identifier; onComplete: RequestComplete }) => {
    const result = useSearchStorageItem(request.term, warehouseId);
    useRequestCompletion(result, onComplete);
    return null;
};

const StorageItemLookup = ({ request, warehouseId, onComplete }: { request: StorageItemRequest; warehouseId: Identifier; onComplete: RequestComplete }) => {
    const result = useGetStorageItem(request.row.ProductId, request.manufacturerProductId, warehouseId);
    useRequestCompletion(result, onComplete);
    return null;
};

const StockCorrectionRequest = ({ request, warehouseId, onComplete }: { request: CorrectionRequest; warehouseId: Identifier; onComplete: RequestComplete }) => {
    const result = useStockCorrection(warehouseId, request.payload);
    useRequestCompletion(result, onComplete);
    return null;
};

const CorrectionStatus = ({ saving, saved }: { saving: boolean; saved: SavedCorrection | null }) => {
    const tone = saved ? 'success' : 'info';
    return (
        <Box component="fieldset" sx={{ m: 0, px: 1, pb: 0.5, pt: 0, minWidth: 0, width: 'fit-content', maxWidth: '100%', border: '1px solid', borderRadius: 1, borderColor: `${tone}.main`, bgcolor: (theme) => alpha(theme.palette[tone].main, 0.06) }}>
            <Typography component="legend" sx={{ px: 0.5, fontSize: '0.65rem', lineHeight: 1.2, color: `${tone}.main` }}>Correction status</Typography>
            <Box role="status" aria-live="polite" aria-atomic="true" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 22, color: `${tone}.main` }}>
                {saving ? <CircularProgress size={18} color="inherit" aria-label="Saving stock correction" /> : saved ? <CheckCircleOutlineIcon sx={{ fontSize: 18 }} /> : <InfoOutlinedIcon sx={{ fontSize: 18 }} />}
                <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{saving ? 'Saving…' : saved ? `Stock set to ${formatNumber(saved.quantity)} at ${saved.location}` : 'Awaiting correction'}</Typography>
            </Box>
        </Box>
    );
};

const StorageItemPicker = ({ scan, rows, onSelect, onClose }: { scan: string; rows: StockCorrectionItem[]; onSelect: (row: StockCorrectionItem) => void; onClose: () => void }) => {
    const [columns, tableSettings] = useStocktakingTable(rows, onSelect);
    const getDetailPanelContent = useCallback(({ row }: { row: StockCorrectionItem }) => <LocationDetailPanel item={row} />, []);
    const getDetailPanelHeight = useCallback(() => 'auto' as const, []);
    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xl" aria-labelledby="stock-correction-picker-title">
            <DialogTitle id="stock-correction-picker-title">Choose a storage item</DialogTitle>
            <DialogContent>
                <Typography sx={{ mb: 2 }}>Multiple items match "{scan}". Expand a row to inspect its locations, then choose an item.</Typography>
                <CustomDataGrid columns={columns} tableSettings={tableSettings} getDetailPanelContent={getDetailPanelContent} getDetailPanelHeight={getDetailPanelHeight} />
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Cancel</Button></DialogActions>
        </Dialog>
    );
};

const LocationDetailPanel = ({ item }: { item: StockCorrectionItem }) => {
    const [columns, tableSettings] = useWarehouseItemLocationsTable(item.WarehouseItemLocations, 'StockCorrectionMatchLocationsTable');
    return <DetailPanelContainer><CustomDataGrid columns={columns} tableSettings={tableSettings} /></DetailPanelContainer>;
};

const ProductImage = ({ imgId, productName }: { imgId?: Identifier | null; productName?: string }) => {
    const [formatIndex, setFormatIndex] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const hasImage = imgId != null && formatIndex < imageFormats.length;
    return (
        // Keep a generous image inside the product card without pushing the details too far down.
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: { xs: 300, sm: 380, md: 420 }, bgcolor: 'background.default', borderRadius: 1, overflow: 'hidden' }}>
            {hasImage ? <>
                {!loaded && <CircularProgress size={28} aria-label="Loading product image" />}
                <Box component="img" src={`https://dacsaimagestorage.blob.core.windows.net/${TENANT}-${ENV}/raw/${imgId}.${imageFormats[formatIndex]}`} alt={productName ?? 'Product'} onLoad={() => setLoaded(true)} onError={() => { setLoaded(false); setFormatIndex((index) => index + 1); }} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', p: 1.5, opacity: loaded ? 1 : 0 }} />
            </> : <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>{productName ? 'No product image available.' : 'Scan a product to view its image.'}</Typography>}
        </Box>
    );
};

export { default as WarehouseFilter } from 'src/components/atoms/WarehouseSelector';

export default StockCorrection;
